import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DashboardControl } from 'devexpress-dashboard';
import * as ko from 'knockout';
import { Item } from 'devextreme-react/toolbar';
import { Button, List, Popover, TextBox, Tooltip } from 'devextreme-react';
import Tabs from 'devextreme-react/tabs';
import { locale } from 'devextreme/localization';
// El dashboard NO resuelve sus textos con el loadMessages de DevExtreme, sino con el localize() de
// analytics-core. Por eso los mensajes (p. ej. "Cargando...") deben cargarse con ESTE loader; con el
// de devextreme el panel seguía mostrando el texto en inglés.
import { loadMessages as loadAnalyticsMessages } from '@devexpress/analytics-core/property-grid/localization/localization';
import esDashboard from '../../localization/dx-dashboard.es.json';
import { useAppSelector } from '../../contexts/hooks';
import useApi from '../../utils/axios-pettitions';
import { ToolbarSimple } from '../../components';
import './dashboards.scss';

// Localización del dashboard (diccionario de analytics-core). Se carga una vez a nivel de módulo.
loadAnalyticsMessages(esDashboard);

type DashItem = { id: string; name: string };

/**
 * Icono sugerido según el nombre del tablero. Mismo criterio que el selector de tableros de control
 * del Header, adaptado a tableros BI (por defecto, gráfico).
 */
const getDashboardIcon = (name?: string) => {
  const n = (name || '').toLowerCase();
  if (n.includes('factura') || n.includes('ingreso') || n.includes('cartera')) return 'money';
  if (n.includes('venta')) return 'cart';
  if (n.includes('paciente')) return 'user';
  if (n.includes('orden')) return 'orderedlist';
  if (n.includes('examen') || n.includes('examenes') || n.includes('estudio')) return 'detailslayout';
  if (n.includes('sede') || n.includes('servicio')) return 'home';
  if (n.includes('tiempo') || n.includes('oportunidad')) return 'clock';
  return 'chart';
};

/**
 * Un tab = un DashboardControl en modo solo-lectura (ViewerOnly). Se monta UNA vez y se mantiene
 * vivo; al cambiar de pestaña solo se oculta con display:none (no se recarga ni se pierde el estado).
 */
const DashboardViewerTab = ({
  dashboardId,
  host,
  token,
  active,
}: {
  dashboardId: string;
  host: string;
  token?: string | null;
  active: boolean;
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current || !host) {
      return;
    }
    locale('es');

    const control = new DashboardControl(ref.current, {
      endpoint: `${host}api/dashboard`,
      workingMode: 'ViewerOnly',
      initialDashboardId: dashboardId,
      // Se usa fetchRemoteService (NO ajaxRemoteService) por la EXPORTACIÓN. Con el manager ajax,
      // el export arma un <form method="POST" target="_blank"> y, como un form submit no puede
      // enviar headers HTTP, DevExpress mete el Authorization como CAMPO OCULTO del formulario:
      // el backend no lo ve como header y AuthorizeLIS responde 401. El manager fetch hace un
      // fetch() real con los headers y descarga por blob, así la exportación queda autenticada.
      // (Mismo motivo por el que el visor/diseñador de reportes fuerza fetch en dxReportingAuth.ts.)
      // OJO: no se pueden declarar ajaxRemoteService y fetchRemoteService a la vez (lanza error).
      fetchRemoteService: {
        headers: {
          ...(token ? { Authorization: token } : {}),
        },
      },
    });
    control.render();

    return () => {
      try {
        control.dispose();
        if (ref.current) {
          ko.cleanNode(ref.current);
          ref.current.innerHTML = '';
        }
      } catch (error) {
        console.warn('Error limpiando el visor de dashboard:', error);
      }
    };
  }, [host, token, dashboardId]);

  return (
    <div className='dashboard-tab-host' style={{ display: active ? 'block' : 'none' }}>
      <div ref={ref} style={{ width: '100%', height: '100%' }} />
    </div>
  );
};

const Index = () => {
  const token = useAppSelector((d) => d.userInfo.Token);
  const { runApi } = useApi();

  const [host, setHost] = useState('');
  const [available, setAvailable] = useState<DashItem[]>([]);
  const [openTabs, setOpenTabs] = useState<DashItem[]>([]);
  const [activeId, setActiveId] = useState<string>('');
  const [showPicker, setShowPicker] = useState(false);
  const [searchText, setSearchText] = useState('');

  const filteredDashboards = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return available;
    return available.filter((d) => d.name.toLowerCase().includes(q));
  }, [available, searchText]);

  // Host de la API (para el endpoint del dashboard).
  useEffect(() => {
    fetch('/config.json')
      .then((r) => r.json())
      .then((d) => setHost(`${d.apiUrl}/`))
      .catch((e) => console.error('Error obteniendo config.json para dashboards:', e));
  }, []);

  // Lista de tableros disponibles (filtrados por tipo DashBoard y permisos de perfil en el backend).
  useEffect(() => {
    runApi('ReportsList/GetDashboardsList', 'GET').then((res) => {
      if (res && res.internalCode < 200 && Array.isArray(res.data)) {
        setAvailable(res.data.map((r: any) => ({ id: String(r.Rp_Id), name: r.Rp_Nombre })));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Abre un tablero en un tab; si ya está abierto, solo lo activa (no lo duplica).
  const openDashboard = useCallback((item: DashItem) => {
    setOpenTabs((prev) => (prev.some((t) => t.id === item.id) ? prev : [...prev, item]));
    setActiveId(item.id);
  }, []);

  const closeTab = useCallback((id: string) => {
    setOpenTabs((prev) => {
      const next = prev.filter((t) => t.id !== id);
      setActiveId((cur) => (cur === id ? (next.length ? next[next.length - 1].id : '') : cur));
      return next;
    });
  }, []);

  const renderTabTitle = (tab: DashItem) => (
    <div className='dashboard-tab-title'>
      <span>{tab.name}</span>
      <i
        className='dx-icon-close dashboard-tab-close'
        onClick={(ev) => {
          ev.stopPropagation();
          closeTab(tab.id);
        }}
      />
    </div>
  );

  // Render de cada tablero en la lista: icono + nombre + marca de "abierto" (ya tiene pestaña).
  const dashboardItemRender = (item: DashItem) => {
    const isOpen = openTabs.some((t) => t.id === item.id);
    return (
      <div className='dashboard-picker-item' title={item.name}>
        <div className='dashboard-item-icon'>
          <i className={`dx-icon dx-icon-${getDashboardIcon(item.name)}`}></i>
        </div>
        <div className='dashboard-item-name'>{item.name}</div>
        {isOpen && <span className='dashboard-item-badge'>abierto</span>}
      </div>
    );
  };

  const picker = (
    <Item location='after' key='dashboard-picker'>
      <div id='DashboardsPicker' className='dashboard-picker-button' onClick={() => setShowPicker((v) => !v)}>
        <Button icon='chart' text='Abrir tablero' stylingMode='text' disabled={available.length === 0} />
        <i className='dx-icon dx-icon-spindown dashboard-picker-arrow'></i>
      </div>
      <Popover
        visible={showPicker}
        target='#DashboardsPicker'
        position='bottom'
        width={400}
        hideOnOutsideClick
        onHiding={() => setShowPicker(false)}
        showCloseButton={false}
      >
        <div className='dashboard-picker-content'>
          <div className='dashboard-picker-search'>
            <TextBox
              placeholder='Buscar tablero...'
              value={searchText}
              onValueChanged={(e) => setSearchText(e.value || '')}
              mode='search'
              stylingMode='outlined'
              width='100%'
            />
          </div>
          <div className='dashboard-picker-list'>
            <List
              dataSource={filteredDashboards}
              keyExpr='id'
              height={300}
              itemRender={dashboardItemRender}
              selectionMode='single'
              selectByClick
              onItemClick={(e) => {
                openDashboard(e.itemData as DashItem);
                setShowPicker(false);
                setSearchText('');
              }}
              noDataText='No se encontraron tableros'
            />
          </div>
        </div>
      </Popover>
      <Tooltip showEvent='mouseenter' hideEvent='mouseleave' position='top' target='#DashboardsPicker'>
        Tableros BI disponibles
      </Tooltip>
    </Item>
  );

  return (
    <ToolbarSimple title='Tableros BI' additionalToolbarContent={[picker]}>
      <div className='dashboard-wrapper'>
        {openTabs.length > 0 && (
          <Tabs
            className='dashboard-tabs'
            keyExpr='id'
            dataSource={openTabs}
            selectedItemKeys={activeId ? [activeId] : []}
            onItemClick={(e) => setActiveId((e.itemData as DashItem).id)}
            itemRender={renderTabTitle}
            showNavButtons
            scrollByContent
          />
        )}

        <div className='dashboard-content'>
          {openTabs.length === 0 ? (
            <div className='dashboard-empty'>
              Selecciona un tablero en <b>Abrir tablero</b> para visualizarlo.
            </div>
          ) : (
            openTabs.map((t) => (
              <DashboardViewerTab
                key={t.id}
                dashboardId={t.id}
                host={host}
                token={token}
                active={t.id === activeId}
              />
            ))
          )}
        </div>
      </div>
    </ToolbarSimple>
  );
};

export default Index;
